import React, { useRef } from 'react';
import { PhoneIcon, CalendarIcon, InstagramIcon, MapPinIcon } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useLanguage, TRANSLATIONS } from '../../context/LanguageContext';
export function FinalCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const {
    language
  } = useLanguage();
  const t = TRANSLATIONS[language || 'en'];
  return <section id="book" className="w-full bg-black py-20 md:py-32 px-4 relative overflow-hidden" data-id="element-56">
      <div className="absolute inset-0 opacity-5" aria-hidden="true" style={{
      backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.1) 40px, rgba(255,255,255,0.1) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.1) 40px, rgba(255,255,255,0.1) 41px)`
    }} data-id="element-57" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[900px] md:h-[900px] rounded-full bg-black border border-white/5 pointer-events-none" aria-hidden="true" data-id="element-58" />
      <div className="absolute left-0 top-0 bottom-0 w-1.5 barber-pole-stripe opacity-60" aria-hidden="true" data-id="element-59" />
      <div className="absolute right-0 top-0 bottom-0 w-1.5 barber-pole-stripe opacity-60" aria-hidden="true" data-id="element-60" />

      <div className="relative z-10 max-w-4xl mx-auto text-center" ref={ref} data-id="element-61">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={isInView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        duration: 0.6
      }} className="flex justify-center mb-8" data-id="element-62">
          <img src="https://cdn.magicpatterns.com/uploads/bJUmpUCmbSxVNo15R16M2H/logo_b.png" alt="Jonathan's Barbershop" className="h-28 md:h-36 w-auto object-contain" data-id="element-63" />
        </motion.div>

        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="text-5xl md:text-7xl font-bold text-white mb-4 leading-none" style={{
        fontFamily: 'Oswald, sans-serif'
      }} data-id="element-64">
          {t.finalcta_headline}
        </motion.h2>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.25
      }} className="text-gray-400 text-xl mb-2" data-id="element-65">
          {t.finalcta_subtext}
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.45
      }} className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 mt-8" data-id="element-66">
          <a href="tel:7864095863" className="group flex items-center gap-3 bg-[#c41e3a] hover:bg-[#a01830] text-white px-10 py-5 rounded font-bold text-xl transition-all duration-200 shadow-[0_4px_24px_rgba(196,30,58,0.4)] hover:shadow-[0_6px_32px_rgba(196,30,58,0.6)] w-full sm:w-auto justify-center" aria-label="Call now" style={{
          fontFamily: 'Oswald, sans-serif',
          letterSpacing: '0.1em'
        }} data-id="element-67">
            <PhoneIcon size={22} data-id="element-68" />
            {t.finalcta_call}: (786) 409-5863
          </a>
          <a href="#book" className="group flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white px-10 py-5 rounded font-bold text-xl transition-all duration-200 w-full sm:w-auto justify-center" aria-label="Book appointment" style={{
          fontFamily: 'Oswald, sans-serif',
          letterSpacing: '0.1em'
        }} data-id="element-69">
            <CalendarIcon size={22} data-id="element-70" />
            {t.finalcta_book}
          </a>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-500 text-sm" data-id="element-71">
          <a href="https://instagram.com/jonathan_s_barbershop" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" aria-label="Follow us on Instagram" data-id="element-72">
            <InstagramIcon size={16} data-id="element-73" />
            <span data-id="element-74">@jonathan_s_barbershop</span>
          </a>
          <span className="hidden sm:block text-[#2a2a2a]" data-id="element-75">|</span>
          <div className="flex items-center gap-2" data-id="element-76">
            <MapPinIcon size={14} className="text-[#c41e3a]" data-id="element-77" />
            <span data-id="element-78">2851 W 68 St Suite 11, Hialeah Gardens, FL</span>
          </div>
          <span className="hidden sm:block text-[#2a2a2a]" data-id="element-79">|</span>
          <div className="flex items-center gap-2" data-id="element-80">
            <span className="w-2 h-2 rounded-full bg-green-400" aria-hidden="true" data-id="element-81" />
            <span className="text-green-400" data-id="element-82">{t.walkins}</span>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.5,
        delay: 0.75
      }} className="mt-16 pt-8 border-t border-[#1c1c1c] flex flex-col items-center gap-4" data-id="element-83">
          <img src="https://cdn.magicpatterns.com/uploads/bJUmpUCmbSxVNo15R16M2H/logo_b.png" alt="Jonathan's Barbershop" className="h-12 w-auto object-contain opacity-60" data-id="element-84" />
          <p className="text-gray-600 text-sm tracking-widest uppercase" style={{
          fontFamily: 'Oswald, sans-serif'
        }} data-id="element-85">
            Jonathan's Barbershop — Hialeah Gardens, FL
          </p>
          <p className="text-gray-700 text-xs tracking-[0.2em] uppercase" data-id="element-86">
            {language === 'es' ? 'Calidad · Precisión · Comunidad' : 'Quality · Precision · Community'}
          </p>
        </motion.div>
      </div>
    </section>;
}